import { TYPE_MAPPING } from '../constants/typeMapping';

// Get Holland type for an item
function getHollandType(item) {
  for (const [type, items] of Object.entries(TYPE_MAPPING)) {
    if (items.includes(item)) {
      return type;
    }
  }
  return null;
}

// Count occurrences in array
function countInArray(array, value) {
  return array.filter(item => item === value).length;
}

// Split array into quartiles
function splitIntoQuartiles(array) {
  const quartiles = [[], [], [], []];
  const quarter = Math.ceil(array.length / 4);
  
  for (let i = 0; i < array.length; i++) {
    const quartileIndex = Math.min(3, Math.floor(i / quarter));
    quartiles[quartileIndex].push(array[i]);
  }
  
  return quartiles;
}

class Stratified4ItemRanker {
  constructor(items) {
    this.items = items;
    this.itemToIdx = Object.fromEntries(items.map((item, idx) => [item, idx]));
    this.nItems = items.length;
    
    // Initialize ranking data structure
    this.ranking = items.map(item => ({
      item,
      score: 0.5, // Initial middle score
      comparisonCount: 0
    }));
    
    // Track all comparisons
    this.comparisons = [];
    
    // Response-time tracking
    this.responseTimings = [];
    this.lastUpdateTime = new Date().getTime();
    
    // Track state for recovery
    this.remainingGroups = [];
    this.mergePhase = false;
    this.mergeQueue = [];
  }
  
  // Create initial stratified groups
  createStratifiedGroups() {
    const groups = [];
    const itemsByType = {};
    const tempItems = [...this.items];
    
    // Group items by Holland type
    Object.keys(TYPE_MAPPING).forEach(type => {
      itemsByType[type] = tempItems.filter(item => 
        TYPE_MAPPING[type].includes(item)
      );
    });
    
    // Create groups of 4 with diverse Holland types
    while (tempItems.length >= 4) {
      const group = [];
      const typesUsed = new Set();
      
      // Try to get different types first
      Object.entries(itemsByType).forEach(([type, items]) => {
        if (group.length >= 4) return;
        
        if (items.length > 0 && !typesUsed.has(type)) {
          const item = items.shift();
          group.push(item);
          typesUsed.add(type);
          
          // Remove from tempItems
          const idx = tempItems.indexOf(item);
          if (idx >= 0) tempItems.splice(idx, 1);
        }
      });
      
      // Fill remaining slots with any items
      while (group.length < 4 && tempItems.length > 0) {
        // Prioritize underrepresented types
        const typeCount = {};
        Object.keys(TYPE_MAPPING).forEach(type => {
          typeCount[type] = group.filter(item => 
            TYPE_MAPPING[type].includes(item)
          ).length;
        });
        
        // Find type with minimum representation
        const minType = Object.entries(typeCount)
          .filter(([type, _]) => itemsByType[type].length > 0)
          .sort(([_, countA], [__, countB]) => countA - countB)[0]?.[0];
        
        if (minType && itemsByType[minType].length > 0) {
          const item = itemsByType[minType].shift();
          group.push(item);
          
          // Remove from tempItems
          const idx = tempItems.indexOf(item);
          if (idx >= 0) tempItems.splice(idx, 1);
        } else if (tempItems.length > 0) {
          // Just take any item if we can't balance types
          group.push(tempItems.shift());
        }
      }
      
      if (group.length === 4) {
        groups.push(group);
      }
    }
    
    // Handle remaining items (less than 4)
    if (tempItems.length > 0) {
      groups[groups.length - 1] = [...groups[groups.length - 1], ...tempItems];
    }
    
    return groups;
  }
  
  // Initialize ranking process
  initializeRanking() {
    const groups = this.createStratifiedGroups();
    this.remainingGroups = [...groups];
    this.mergePhase = false;
    this.mergeQueue = [];
    
    return this.remainingGroups[0];
  }
  
  // Handle a ranked group from the user
  addRankedGroup(group, ranking) {
    // Track response time
    const currentTime = new Date();
    const responseTime = currentTime - this.lastUpdateTime;
    this.responseTimings.push(responseTime);
    this.lastUpdateTime = currentTime;
    
    // Store this comparison
    this.comparisons.push({
      group,
      ranking,
      timestamp: new Date().toISOString()
    });
    
    // Update ranking scores based on position (higher is better)
    ranking.forEach((item, position) => {
      const itemIdx = this.itemToIdx[item];
      // Position is 0-indexed, so we invert: (4 - position) / 4
      const positionScore = (4 - position) / 4;
      
      // Update our ranking with an exponential moving average
      const itemRanking = this.ranking.find(r => r.item === item);
      if (itemRanking) {
        const weight = 1 / (itemRanking.comparisonCount + 1);
        itemRanking.score = (itemRanking.score * (1 - weight)) + (positionScore * weight);
        itemRanking.comparisonCount++;
      }
    });
    
    // Process the group queue management
    if (this.mergePhase) {
      // If we're in merge phase, remove from merge queue
      if (this.mergeQueue.length > 0) {
        this.mergeQueue.shift();
        console.log("Removed group from merge queue, remaining:", this.mergeQueue.length);
      }
    } else {
      // If not in merge phase, remove from remaining groups
      this.remainingGroups.shift();
    }
    
    // If we've ranked all initial groups, start merging
    if (this.remainingGroups.length === 0 && !this.mergePhase) {
      this.mergePhase = true;
      this.prepareMergePhase();
      console.log("Entering merge phase with queue:", this.mergeQueue.length);
    }
    
    // Return next group to compare or null if done
    if (this.mergePhase && this.mergeQueue.length > 0) {
      return this.mergeQueue[0];
    } else if (this.remainingGroups.length > 0) {
      return this.remainingGroups[0];
    }
    
    console.log("All comparisons complete!");
    return null; // We're done with all comparisons
  }
  
  // Prepare for merge phase
  prepareMergePhase() {
    // Sort all items by current score
    const sortedItems = [...this.ranking].sort((a, b) => b.score - a.score);
    const sortedItemNames = sortedItems.map(r => r.item);
    
    // Create merge groups that span the ranking with type diversity
    this.mergeQueue = [];
    
    // Generate diverse quartile-spanning groups for comparison
    const quartiles = splitIntoQuartiles(sortedItemNames);
    
    // Create groups with one item from each quartile
    for (let i = 0; i < Math.min(4, quartiles[0].length); i++) {
      const group = [
        quartiles[0][i % quartiles[0].length],
        quartiles[1][i % quartiles[1].length],
        quartiles[2][i % quartiles[2].length],
        quartiles[3][i % quartiles[3].length]
      ];
      this.mergeQueue.push(group);
    }
    
    // Create a few groups that focus on high-uncertainty items
    const uncertaintyRanking = this.calculateUncertainty();
    const highUncertaintyItems = uncertaintyRanking.slice(0, 8);
    
    // Create 2 groups of high-uncertainty items
    if (highUncertaintyItems.length >= 4) {
      this.mergeQueue.push(highUncertaintyItems.slice(0, 4));
      if (highUncertaintyItems.length >= 8) {
        this.mergeQueue.push(highUncertaintyItems.slice(4, 8));
      }
    }
  }
  
  // Calculate uncertainty for each item
  calculateUncertainty() {
    // Uncertainty is inversely proportional to comparison count
    // and higher for items in the middle of the ranking
    return this.ranking.map(item => {
      const normalizedPosition = Math.abs(item.score - 0.5) * 2; // 0 for middle, 1 for extremes
      const uncertaintyScore = (1 - normalizedPosition) * (1 + 1 / Math.sqrt(item.comparisonCount + 1));
      return {
        item: item.item,
        uncertainty: uncertaintyScore
      };
    }).sort((a, b) => b.uncertainty - a.uncertainty)
      .map(r => r.item);
  }
  
  // Compute final rankings
  computeRankings() {
    const scores = [...this.ranking].sort((a, b) => b.score - a.score);
    
    // Calculate ranks (with ties)
    let currentRank = 1;
    let previousScore = null;
    
    return scores.map((s, idx) => {
      if (previousScore !== null && s.score !== previousScore) {
        currentRank = idx + 1;
      }
      previousScore = s.score;
      
      const category = s.score >= 2/3 ? "HIGH" :
                      s.score >= 1/3 ? "MEDIUM" : "LOW";
      
      return {
        item: s.item,
        score: s.score,
        rank: currentRank,
        category,
        confidenceInterval: [
          Math.max(0, s.score - 0.1),
          Math.min(1, s.score + 0.1)
        ]
      };
    });
  }
  
  // Calculate coherence (consistency of rankings)
  calculateCoherence() {
    if (this.comparisons.length < 2) return 1;
    
    // For 4-item rankings, coherence is measured by consistency
    // between individual item orderings across comparisons
    let inconsistencies = 0;
    let totalPairs = 0;
    
    // Extract all implied pairwise comparisons from our rankings
    const impliedPairs = [];
    
    this.comparisons.forEach(comparison => {
      const ranking = comparison.ranking;
      for (let i = 0; i < ranking.length; i++) {
        for (let j = i + 1; j < ranking.length; j++) {
          impliedPairs.push({
            higher: ranking[i],
            lower: ranking[j]
          });
          totalPairs++;
        }
      }
    });
    
    // Count inconsistencies
    for (let i = 0; i < impliedPairs.length; i++) {
      for (let j = i + 1; j < impliedPairs.length; j++) {
        const pair1 = impliedPairs[i];
        const pair2 = impliedPairs[j];
        
        // If same items but different ordering
        if (pair1.higher === pair2.lower && pair1.lower === pair2.higher) {
          inconsistencies++;
        }
      }
    }
    
    return totalPairs > 0 ? 1 - (inconsistencies / totalPairs) : 1;
  }
  
  // Calculate nuance (variation in responses)
  calculateNuance() {
    if (this.comparisons.length === 0) return 0;
    
    // For 4-item rankings, we consider variety of rankings
    // and distribution of items across positions
    
    // Calculate entropy of position distribution for each item
    const positionCounts = {};
    this.items.forEach(item => {
      positionCounts[item] = [0, 0, 0, 0]; // Counts in positions 0,1,2,3
    });
    
    this.comparisons.forEach(comparison => {
      comparison.ranking.forEach((item, position) => {
        positionCounts[item][position]++;
      });
    });
    
    // Calculate entropy for each item
    const entropies = [];
    Object.entries(positionCounts).forEach(([item, counts]) => {
      const total = counts.reduce((sum, count) => sum + count, 0);
      if (total === 0) return;
      
      const entropy = counts.reduce((entropy, count) => {
        if (count === 0) return entropy;
        const probability = count / total;
        return entropy - (probability * Math.log2(probability));
      }, 0) / Math.log2(4); // Normalize by max entropy
      
      entropies.push(entropy);
    });
    
    // Average entropy across all items
    return entropies.reduce((sum, entropy) => sum + entropy, 0) / entropies.length;
  }
  
  // Get current state (for saving)
  getState() {
    return {
      ranking: [...this.ranking],
      comparisons: [...this.comparisons],
      responseTimings: [...this.responseTimings],
      lastUpdateTime: this.lastUpdateTime,
      remainingGroups: [...this.remainingGroups],
      mergePhase: this.mergePhase,
      mergeQueue: [...this.mergeQueue]
    };
  }
  
  // Set state (for restoring)
  setState(state) {
    this.ranking = state.ranking;
    this.comparisons = state.comparisons;
    this.responseTimings = state.responseTimings;
    this.lastUpdateTime = state.lastUpdateTime;
    this.remainingGroups = state.remainingGroups;
    this.mergePhase = state.mergePhase;
    this.mergeQueue = state.mergeQueue;
  }
}

export default Stratified4ItemRanker; 